import type { ArConfig } from '../types/config';

/**
 * Parameters for resolving AR asset URLs at runtime.
 *
 * Not all fields are always available — for instance `interiorId` is only
 * present when the model defines `interiorVariants`.  The resolver handles
 * missing values gracefully (falls back to static URLs or strips unused
 * template placeholders).
 */
export interface ArResolveParams {
  /** Current steering side resolved from the market config. */
  steering: 'lhd' | 'rhd';
  /** Currently selected exterior color id (e.g. "tango-red"). */
  colorId: string;
  /** Currently selected interior/pano variant id (e.g. "baikalblau"). May be empty. */
  interiorId?: string;
  /** The model's main GLB URL (already steering-resolved). Used as Android fallback. */
  mainGlbUrl: string;
}

export interface ArResolvedUrls {
  /** USDZ URL for iOS Quick Look (empty string when unavailable). */
  usdzUrl: string;
  /** GLB URL for Android WebXR model-viewer. */
  glbUrl: string;
  /** Whether coloring should be applied to the model-viewer GLB after load. */
  needsColoring: boolean;
}

/**
 * Resolves the final AR asset URLs based on the `ar` config block and
 * the user's current color / interior / steering selection.
 *
 * ## Static mode (default)
 * Returns `iosUsdzUrl` and `androidGlbUrl` as-is — one fixed model.
 *
 * ## Colored mode
 * - **iOS**: interpolates `iosUsdzPattern` with `{steering}`, `{colorId}`,
 *   `{interiorId}` placeholders.
 * - **Android**: picks the GLB from `androidGlb.{steering}` → `androidGlb.lhd`
 *   → `mainGlbUrl` (in that priority order). Sets `needsColoring = true` so
 *   the caller knows to apply materials via model-viewer's Scene Graph API.
 */
export function resolveArUrls(
  arConfig: ArConfig | undefined,
  params: ArResolveParams,
): ArResolvedUrls {
  if (!arConfig) {
    return { usdzUrl: '', glbUrl: params.mainGlbUrl, needsColoring: false };
  }

  const mode = arConfig.mode ?? 'static';

  if (mode === 'static') {
    return {
      usdzUrl: arConfig.iosUsdzUrl ?? '',
      glbUrl: arConfig.androidGlbUrl ?? params.mainGlbUrl,
      needsColoring: false,
    };
  }

  // ── colored mode ─────────────────────────────────────────────────────

  // iOS — interpolate URL template
  let usdzUrl = '';
  if (arConfig.iosUsdzPattern) {
    usdzUrl = arConfig.iosUsdzPattern
      .replace(/\{steering\}/g, params.steering)
      .replace(/\{colorId\}/g, params.colorId)
      .replace(/\{interiorId\}/g, params.interiorId ?? '');
  }

  // Android — resolve GLB with steering fallback chain
  let glbUrl: string;
  if (arConfig.androidGlb) {
    const side = params.steering;
    glbUrl =
      side === 'rhd' && arConfig.androidGlb.rhd ? arConfig.androidGlb.rhd : arConfig.androidGlb.lhd;
  } else {
    glbUrl = params.mainGlbUrl;
  }

  return { usdzUrl, glbUrl, needsColoring: true };
}
